import { useCallback, useMemo } from "react";
import { NavigateFunction } from "react-router-dom";

import { TableColumnsType } from "antd";
import dayjs from "dayjs";

import StatusBadges from "../../StatusBadges";
import DropdownOptionsActions from "../components/DropdownOptionsActions";
import { ActionPartial } from "../../../../types/actions";

type Options = {
  navigate: NavigateFunction;
  onDelete: (id: number) => void;
  actions: ActionPartial[];
  isInPageEvent?: boolean;
  iAmTheAssignedPilote?: boolean;
  isColumnForPageEvent?: boolean;
  parentType?: "event" | "constat" | "project";
};

export default function useTableActions({
  navigate,
  onDelete,
  actions,
  iAmTheAssignedPilote = false,
  isColumnForPageEvent,
  parentType = "event",
}: Options) {
  const parentBasePath =
    parentType === "constat"
      ? "/constats"
      : parentType === "project"
        ? "/projects"
        : "/events";

  const columns = useMemo(() => {
    const _columns: TableColumnsType<ActionPartial> = isColumnForPageEvent
      ? [
          {
            title: "TITRE",
            dataIndex: "description",
            key: "1",
            align: "left",
            width: 500,
          },
          {
            title: "DATE D'ÃCHÃANCE",
            dataIndex: "dateAsked",
            key: "2",
            align: "center",
            width: 200,
            render: (value) => dayjs(value).format("DD/MM/YYYY"),
            sorter: (a, b) =>
              dayjs(a.dateAsked).isBefore(b.dateAsked) ? -1 : 1,
          },
          {
            title: "ATTRIBUTION",
            dataIndex: ["performer", "fullname"],
            key: "3",
            align: "center",
            width: 200,
            className: "text-light-blue",
          },
          {
            title: "STATUT",
            dataIndex: "status",
            key: "3",
            align: "center",
            width: 100,
            render: (value) => <StatusBadges value={value} type="actions" />,
          },
          {
            title: "",
            dataIndex: "",
            align: "center",
            key: "7",
            width: 10,
            render: (_, item) => (
              <DropdownOptionsActions
                itemId={item.id}
                iAmTheAssignedPilote={iAmTheAssignedPilote}
                goToDetails={(id) => {
                  const eventId = actions.find(
                    (action) => action.id === id,
                  )?.eventId;
                  navigate(`${parentBasePath}/${eventId}/actions/${id}`);
                }}
                onModify={(id) => {
                  const eventId = actions.find(
                    (action) => action.id === id,
                  )?.eventId;
                  navigate(`${parentBasePath}/${eventId}/actions/${id}/edit`);
                }}
                onDelete={(id) => onDelete(id)}
              />
            ),
          },
        ]
      : [
          {
            title: "TITRE",
            dataIndex: "title",
            key: "1",
            align: "left",
            width: 300,
            sorter: true,
            sortDirections: ["ascend", "descend"],
          },
          {
            title: "ÃVÃNEMENT",
            dataIndex: "eventName",
            key: "2",
            align: "center",
            width: 200,
            sorter: true,
            sortDirections: ["ascend", "descend"],
          },
          {
            title: "DATE",
            dataIndex: "dateAsked",
            key: "3",
            align: "center",
            width: 100,
            render: (value) => dayjs(value).format("DD/MM/YYYY"),
            sorter: (a, b) =>
              dayjs(a.dateAsked).isBefore(b.dateAsked) ? -1 : 1,
            sortDirections: ["ascend", "descend"],
          },

          {
            title: "LIEU",
            dataIndex: "eventLocation",
            align: "center",
            key: "4",
            width: 100,
            sorter: true,
            sortDirections: ["ascend", "descend"],
          },

          {
            title: "GRAVITÃ",
            dataIndex: "eventGravity",
            align: "center",
            key: "5",
            width: 100,
            sorter: true,
            sortDirections: ["ascend", "descend"],
          },

          {
            title: "STATUT",
            dataIndex: "status",
            align: "center",
            key: "6",
            width: 100,
            render: (value) => <StatusBadges value={value} type="actions" />,
            sorter: true,
            sortDirections: ["ascend", "descend"],
          },
          {
            title: "",
            dataIndex: "",
            align: "center",
            key: "7",
            width: 10,
            render: (_, item) => (
              <DropdownOptionsActions
                itemId={item.id}
                iAmTheAssignedPilote={iAmTheAssignedPilote}
                goToDetails={(id) => {
                  const eventId = actions.find(
                    (action) => action.id === id,
                  )?.eventId;
                  navigate(`${parentBasePath}/${eventId}/actions/${id}`);
                }}
                onModify={(id) => {
                  const eventId = actions.find(
                    (action) => action.id === id,
                  )?.eventId;
                  navigate(`${parentBasePath}/${eventId}/actions/${id}/edit`);
                }}
                onDelete={(id) => onDelete(id)}
              />
            ),
          },
        ];

    return _columns;
  }, [
    navigate,
    onDelete,
    iAmTheAssignedPilote,
    actions,
    isColumnForPageEvent,
    parentBasePath,
  ]);

  const onRowClick = useCallback(
    (record: ActionPartial) => {
      return {
        onClick: () =>
          navigate(`${parentBasePath}/${record.eventId}/actions/${record.id}`),
      };
    },
    [navigate, parentBasePath],
  );

  return { columns, onRowClick };
}
